import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Snackbar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { useThemeLayout } from '../../contexts/ThemeLayoutContext';

const SiteSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user } = useAuthStore();
  const { currentTheme, currentLayout, availableThemes, availableLayouts, changeTheme, changeLayout } = useThemeLayout();
  const [activeTab, setActiveTab] = useState(0);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [settings, setSettings] = useState({
    logo: '',
    aboutTitle: 'DomikTyumen — ваш надежный партнер',
    aboutDescription: '',
    aboutGuarantees: 'Честные условия аренды, прозрачные цены и отсутствие скрытых платежей.',
    contactPhone: '+7 (999) 123-45-67',
    contactEmail: 'info@domiktyumen.ru',
    contactAddress: 'г. Тюмень, ул. Примерная, 123',
    siteTheme: 'blue',
    homeLayout: 'classic',
    heroTheme: 'auto' // 'auto', 'winter', 'spring', 'summer', 'autumn', 'newYear', 'may9'  
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/settings', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && Object.keys(response.data).length > 0) {
          // Update settings with values from API, keeping defaults for missing values
          setSettings(prev => ({
            ...prev,
            ...response.data
          }));
          
          if (response.data.logo) {
            setLogoPreview(response.data.logo);
          }
          
          // Update theme and layout in context if they exist in settings
          if (response.data.siteTheme && availableThemes.includes(response.data.siteTheme)) {
            changeTheme(response.data.siteTheme);
          }
          
          if (response.data.homeLayout && availableLayouts.includes(response.data.homeLayout)) {
            changeLayout(response.data.homeLayout);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching settings:', error);
        setError('Ошибка при загрузке настроек');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [availableThemes, availableLayouts, changeTheme, changeLayout]);

  // Handle logo file change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async () => {
    if (!logo) {
      setError('Пожалуйста, выберите файл логотипа');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', logo);
      
      // Отправляем запрос на сервер для сохранения файла на VPS
      const response = await axios.put('/api/settings/logo/local', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      // Получаем URL логотипа с сервера
      const logoUrl = response.data.logo; // Убираем временную метку, чтобы избежать проблем с загрузкой
      
      setLogoPreview(logoUrl);
      setLogo(null);
      setSuccess(true);
      setSuccessMessage('Логотип успешно обновлен');
      setLoading(false);
      
      // Update settings with new logo path
      setSettings(prev => ({
        ...prev,
        logo: logoUrl
      }));
      
      // Force reload of logo in all components
      const logoUpdateEvent = new CustomEvent('logoUpdated', { 
        detail: { logo: logoUrl } 
      });
      window.dispatchEvent(logoUpdateEvent);
      
      // Перезагрузка страницы для обновления логотипа
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Ошибка при загрузке логотипа');
      setLoading(false);
    }
  };
  
  // Handle text settings update
  const handleSettingsUpdate = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const settingsToUpdate = {
        aboutTitle: settings.aboutTitle,
        aboutDescription: settings.aboutDescription,
        aboutGuarantees: settings.aboutGuarantees,
        contactPhone: settings.contactPhone,
        contactEmail: settings.contactEmail,
        contactAddress: settings.contactAddress
      };
      
      // Add theme and layout settings if we're on the appearance tab
      if (activeTab === 3) {
        settingsToUpdate.siteTheme = settings.siteTheme;
        settingsToUpdate.homeLayout = settings.homeLayout;
        settingsToUpdate.heroTheme = settings.heroTheme;
      }
      
      const response = await axios.put('/api/settings', settingsToUpdate, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setSettings(prev => ({
        ...prev,
        ...response.data
      }));
      
      // Update theme and layout in context if they were changed
      if (activeTab === 3) {
        if (settings.siteTheme && settings.siteTheme !== currentTheme) {
          changeTheme(settings.siteTheme);
        }
        
        if (settings.homeLayout && settings.homeLayout !== currentLayout) {
          changeLayout(settings.homeLayout);
        }
      }
      
      setSuccess(true);
      setSuccessMessage('Настройки успешно сохранены');
      setLoading(false);
      
      // Dispatch event for other components to update
      const settingsUpdateEvent = new CustomEvent('settingsUpdated', { 
        detail: response.data 
      });
      window.dispatchEvent(settingsUpdateEvent);
      
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Ошибка при сохранении настроек');
      setLoading(false);
    }
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSuccess(false);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Настройки сайта
      </Typography>
      
      <Divider sx={{ mb: 4 }} />
      
      <Tabs 
        value={activeTab} 
        onChange={handleTabChange} 
        variant="scrollable" 
        scrollButtons="auto"
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          '.MuiTab-root': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            minHeight: { xs: 48, sm: 64 },
            px: { xs: 1, sm: 2 }
          }
        }}
      >
        <Tab icon={<ImageIcon />} label="Логотип" />
        <Tab icon={<DescriptionIcon />} label="О нас" />
        <Tab icon={<ContactMailIcon />} label="Контакты" />
        <Tab icon={<DesignServicesIcon />} label="Внешний вид" />
      </Tabs>
      
      {/* Логотип */}
      {activeTab === 0 && (
        <Grid container spacing={{ xs: 2, sm: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                Логотип сайта
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Загрузите логотип, который будет отображаться в шапке сайта и на главной странице.
                Рекомендуемый размер: 200x60 пикселей, формат PNG с прозрачным фоном.
              </Typography>
              
              <Card sx={{ mb: 3, maxWidth: 400 }}>
                <CardMedia
                  component="img"
                  image={logoPreview || '/assets/logo/logo.png'}
                  alt="Логотип сайта"
                  sx={{ 
                    height: 100, 
                    objectFit: 'contain',
                    backgroundColor: '#f5f5f5',
                    p: 2
                  }}
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Текущий логотип сайта
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    size={isMobile ? "small" : "medium"}
                    sx={{ mr: 1 }}
                  >
                    Выбрать файл
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleLogoChange}
                    />
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    size={isMobile ? "small" : "medium"}
                    onClick={handleLogoUpload}
                    disabled={!logo || loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Сохранить'}
                  </Button>
                </CardActions>
              </Card>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* О нас */}
      {activeTab === 1 && (
        <Grid container spacing={{ xs: 2, sm: 4 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                Раздел "О нас"
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Настройте содержимое раздела "О нас" на главной странице сайта.
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="aboutTitle">Заголовок</InputLabel>
                <OutlinedInput
                  id="aboutTitle"
                  name="aboutTitle"
                  value={settings.aboutTitle || ''}
                  onChange={handleInputChange}
                  label="Заголовок"
                />
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="aboutDescription">Описание</InputLabel>
                <OutlinedInput
                  id="aboutDescription"
                  name="aboutDescription"
                  value={settings.aboutDescription || ''}
                  onChange={handleInputChange}
                  label="Описание"
                  multiline
                  rows={6}
                />
                <FormHelperText>Введите основной текст для раздела "О нас"</FormHelperText>
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="aboutGuarantees">Гарантии</InputLabel>
                <OutlinedInput
                  id="aboutGuarantees"
                  name="aboutGuarantees"
                  value={settings.aboutGuarantees || ''}
                  onChange={handleInputChange}
                  label="Гарантии"
                  multiline
                  rows={3}
                />
                <FormHelperText>Текст в блоке "Наши гарантии"</FormHelperText>
              </FormControl>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSettingsUpdate}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Сохранить'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Контакты */}
      {activeTab === 2 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Контактная информация
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Укажите контактную информацию, которая будет отображаться на сайте.
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="contactPhone">Телефон</InputLabel>
                <OutlinedInput
                  id="contactPhone"
                  name="contactPhone"
                  value={settings.contactPhone || ''}
                  onChange={handleInputChange}
                  label="Телефон"
                />
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="contactEmail">Email</InputLabel>
                <OutlinedInput
                  id="contactEmail"
                  name="contactEmail"
                  value={settings.contactEmail || ''}
                  onChange={handleInputChange}
                  label="Email"
                  type="email"
                />
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="contactAddress">Адрес</InputLabel>
                <OutlinedInput
                  id="contactAddress"
                  name="contactAddress"
                  value={settings.contactAddress || ''}
                  onChange={handleInputChange}
                  label="Адрес"
                />
              </FormControl>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSettingsUpdate}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Сохранить'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Внешний вид */}
      {activeTab === 3 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Тема оформления
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Выберите цветовую тему для вашего сайта.
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="theme-select-label">Тема оформления</InputLabel>
                <Select
                  labelId="theme-select-label"
                  id="theme-select"
                  value={settings.siteTheme}
                  name="siteTheme"
                  onChange={handleInputChange}
                  label="Тема оформления"
                >
                  <MenuItem value="blue">Синяя (стандартная)</MenuItem>
                  <MenuItem value="green">Зеленая</MenuItem>
                  <MenuItem value="dark">Темная</MenuItem>
                </Select>
                <FormHelperText>Выбранная тема будет применена ко всему сайту</FormHelperText>
              </FormControl>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="hero-theme-select-label">Тема для главного баннера</InputLabel>
                <Select
                  labelId="hero-theme-select-label"
                  id="hero-theme-select"
                  value={settings.heroTheme}
                  name="heroTheme"
                  onChange={handleInputChange}
                  label="Тема для главного баннера"
                >
                  <MenuItem value="auto">Автоматически (по сезону/празднику)</MenuItem>
                  <MenuItem value="winter">Зима</MenuItem>
                  <MenuItem value="spring">Весна</MenuItem>
                  <MenuItem value="summer">Лето</MenuItem>
                  <MenuItem value="autumn">Осень</MenuItem>
                  <MenuItem value="night">Ночь</MenuItem>
                  <MenuItem value="newYear">Новый год</MenuItem>
                  <MenuItem value="may9">День Победы</MenuItem>
                </Select>
                <FormHelperText>Выберите тему для главного баннера на главной странице</FormHelperText>
              </FormControl>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Предпросмотр темы:  
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: settings.siteTheme === 'blue' ? '#1E4D8C' : 
                           settings.siteTheme === 'green' ? '#2E7D32' : 
                           settings.siteTheme === 'dark' ? '#121212' : '#1E4D8C',
                  color: settings.siteTheme === 'dark' ? '#FFFFFF' : '#FFFFFF',
                  mb: 2
                }}>
                  <Typography variant="subtitle1">
                    Основной цвет
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: settings.siteTheme === 'blue' ? '#FF6B6B' : 
                           settings.siteTheme === 'green' ? '#FF9800' : 
                           settings.siteTheme === 'dark' ? '#F48FB1' : '#FF6B6B',
                  color: '#FFFFFF',
                  mb: 2
                }}>
                  <Typography variant="subtitle1">
                    Дополнительный цвет
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: settings.siteTheme === 'blue' ? '#F8FAFC' : 
                           settings.siteTheme === 'green' ? '#F5F9F5' : 
                           settings.siteTheme === 'dark' ? '#1E1E1E' : '#F8FAFC',
                  color: settings.siteTheme === 'dark' ? '#FFFFFF' : '#2C3E50',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle1">
                    Фоновый цвет
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Макет главной страницы
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Выберите макет для отображения секций на главной странице сайта.
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <RadioGroup
                  name="homeLayout"
                  value={settings.homeLayout}
                  onChange={handleInputChange}
                >
                  <FormControlLabel 
                    value="classic" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="subtitle1">Классический</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Стандартный вертикальный макет с секциями, расположенными одна под другой.
                        </Typography>
                      </Box>
                    } 
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel 
                    value="modern" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="subtitle1">Современный</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Двухколоночный макет с основным контентом слева и боковой информацией справа.
                        </Typography>
                      </Box>
                    } 
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel 
                    value="minimalist" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="subtitle1">Минималистичный</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Упрощенный макет с минимальным количеством элементов и отступов.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSettingsUpdate}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Сохранить настройки внешнего вида'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar open={success} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteSettings;
